import {
    ISessionCoolDownHandler
} from "../session-cooldown-handler.interface";
import {IServiceFactoryExtended} from "../../../../service-factory-extended.interface";
import {Check, TimeSpan} from "@sparkware/app-utils";
import {NullableNumber} from "../../../../../../common/types/nullable-types";
import {makeObservable, observable, runInAction} from "mobx";
import {
    SessionCooldownPopupComponent
} from "../../../../../components/session-cooldown-popup/session-cooldown-popup.component";
import React from "react";
import {namedSetInterval} from "../../../../../../common/utils/time-functions";
import {IModalDialogHandler} from "../../../../dialog/dialog.service.interface";

const COOL_DOWN_EXPIRATION_DATE_STORAGE_KEY: string = "orbit-session-cooldown-expiration-date";

export class GermanySessionCoolDownHandler implements ISessionCoolDownHandler {
    constructor(private readonly services: IServiceFactoryExtended) {
        try {
            this._coolDownTime = TimeSpan.parse(services.configuration.servicesConfigs.Regulation.sessionCooldownTime);
        } catch (err) {
            this.services.logger.error('Failed to parse Germany session cool down time', err);
            this._coolDownTime = TimeSpan.fromMinutes(5);
        }

        makeObservable(this);
    }

    private readonly _coolDownTime: TimeSpan;

    @observable.ref
    private _remainingTime: TimeSpan = TimeSpan.fromMilliseconds(0);

    private _dialogHandler: IModalDialogHandler | null = null;

    dispose(): void {
        this._stopCountDownTimer();
        this._dialogHandler?.close();
        this.coolDownExpirationDate = null;
    }

    async applyRestriction(): Promise<void> {
        try {
            await this.services.api.restrictionsController.setCoolDownRestriction({
                CoolDownTimeInSeconds: this._coolDownTime.totalSeconds
            });
        } catch (err){
            this.services.logger.error('Failed to set cool down restriction', err);
        }
    }
    showCoolDown(): void {
        this.coolDownExpirationDate = this.services.time.now() + this._coolDownTime.totalMilliseconds;
        this._showCoolDownDialog();
    }

    continueCoolDown(): void {
        if(this.isInCoolDown && !this._dialogHandler) {
            this._showCoolDownDialog();
        }
    }

    private get isInCoolDown(): boolean {
        const expirationDate = this.coolDownExpirationDate;
        if(Check.isNullOrUndefined(expirationDate)) {
            return false;
        }

        return this.services.time.now() < expirationDate;
    }

    private get coolDownExpirationDate(): NullableNumber {
        const storedExpirationDate = this.services.localStorage.getItem(COOL_DOWN_EXPIRATION_DATE_STORAGE_KEY);
        if(storedExpirationDate) {
            return parseInt(storedExpirationDate);
        }

        return null;
    }

    private set coolDownExpirationDate(value: NullableNumber) {
        if(Check.isNullOrUndefined(value)) {
            this.services.localStorage.removeItem(COOL_DOWN_EXPIRATION_DATE_STORAGE_KEY);
        } else {
            this.services.localStorage.setItem(COOL_DOWN_EXPIRATION_DATE_STORAGE_KEY, value);
        }
    }

    private _updateRemainingTime(): void {

        runInAction(() => {
            if(this.isInCoolDown) {
                this._remainingTime = TimeSpan.fromMilliseconds(this.coolDownExpirationDate - this.services.time.now());
            } else {
                this._remainingTime = TimeSpan.fromMilliseconds(0);
                this.coolDownExpirationDate = null;
            }

        });
    }

    private _showCoolDownDialog(): void  {

        this.services.analytics.sendElasticInfoEvent({
            eventType:"Lugas",
            category:"SESSION_COOLDOWN_POPUP",
            action:"SHOW_POPUP"
        }, false);

        this._startCountDownTimer();

        this.services.dialog.showModal((dialogHandler) => {
            this._dialogHandler = dialogHandler;
            return <SessionCooldownPopupComponent getRemainingTime={() => this._remainingTime} />
        }, {
            title: this.services.translation.translate("SESSION_COOLDOWN_POPUP_TITLE"),
            isHighPriority: true,
            hideCloseButton: true,
            shouldBeClosedOnlyByUser: true
        });
    }

    private _countDownTimerRef: any = null;
    private _startCountDownTimer(): void {
        this._updateRemainingTime();
        this._countDownTimerRef = namedSetInterval('COOL_DOWN_COUNT_DOWN_TIMER',async () => {
            if(this.isInCoolDown) {
                this._updateRemainingTime();
            } else {
                this._closeCoolDownDialog();
            }
        }, 1000);
    }

    private _stopCountDownTimer(): void {
        if(this._countDownTimerRef) {
            clearInterval(this._countDownTimerRef);
            this._countDownTimerRef = null;
        }
    }

    private _closeCoolDownDialog(): void {
        this._stopCountDownTimer();
        if(this._dialogHandler) {
            this._dialogHandler.close();
            this._dialogHandler = null;
            this.services.analytics.sendElasticInfoEvent({
                eventType:"Lugas",
                category:"SESSION_COOLDOWN_POPUP",
                action:"HIDE_POPUP"
            }, false);

        }

    }

}